import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Stats.css'; // Import custom CSS
import API_URL from '../../api/ExamCalendarApi';

const Stats = () => {
  const [stats, setStats] = useState([]);
  const [formData, setFormData] = useState({
    id: '',
    value: '',
    logo: null,
  });
  const [editStatId, setEditStatId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false); // Add loading state

  // Fetch all stats
  const fetchStats = async () => {
    setLoading(true); // Set loading to true
    try {
      const response = await axios.get(`${API_URL}/homepage/get-stats`);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setErrorMessage('Failed to fetch stats.');
    }
    setLoading(false); // Set loading to false
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: files ? files[0] : value,
    }));
  };

  // Handle form submit (Add or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Show loading spinner during submit

    const formPayload = new FormData();
    formPayload.append('id', formData.id);
    formPayload.append('value', formData.value);
    if (formData.logo) {
      formPayload.append('logo', formData.logo);
    }

    try {
      if (editStatId) {
        // Update stat using the correct 'id' field
        await axios.put(`${API_URL}/homepage/update-stat/${editStatId}`, formPayload);
        setEditStatId(null);
      } else {
        // Add new stat
        await axios.post(`${API_URL}/homepage/add-stat`, formPayload);
      }
      setFormData({ id: '', value: '', logo: null });
      await fetchStats(); // Fetch updated stats list after successful submission
    } catch (error) {
      console.error('Error saving stat:', error);
      setErrorMessage('Failed to save stat.');
    }
    setLoading(false); // Hide loading spinner
  };

  // Handle delete stat (using the correct 'id' field)
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this stat?')) {
      setLoading(true); // Show loading spinner during delete
      try {
        await axios.delete(`${API_URL}/homepage/delete-stat/${id}`);
        await fetchStats(); // Fetch updated stats list after delete
      } catch (error) {
        console.error('Error deleting stat:', error);
        setErrorMessage('Failed to delete stat.');
      }
      setLoading(false); // Hide loading spinner
    }
  };

  // Handle edit stat (populate the form for editing)
  const handleEdit = (stat) => {
    setEditStatId(stat.id); // Correctly set 'id' for editing
    setFormData({
      id: stat.id,
      value: stat.value,
      logo: null, // Reset logo as it's not needed unless updating
    });
  };

  return (
    <div className="admin-stats-container">
      <h2 className="admin-stats-title">Institution Stats Management</h2>

      {errorMessage && <div className="admin-stats-error">{errorMessage}</div>}

      <form onSubmit={handleSubmit} className="admin-stats-form">
        <div className="admin-stats-form-group">
          <label className="admin-stats-label">Stat ID</label>
          <input
            type="text"
            name="id"
            className="admin-stats-input"
            value={formData.id}
            onChange={handleChange}
            required
          />
        </div>

        <div className="admin-stats-form-group">
          <label className="admin-stats-label">Stat Value</label>
          <input
            type="text"
            name="value"
            className="admin-stats-input"
            value={formData.value}
            onChange={handleChange}
            required
          />
        </div>

        <div className="admin-stats-form-group">
          <label className="admin-stats-label">Stat Logo</label>
          <input
            type="file"
            name="logo"
            className="admin-stats-input"
            onChange={handleChange}
          />
        </div>

        <button type="submit" className="admin-stats-button">
          {editStatId ? 'Update Stat' : 'Add Stat'}
        </button>
      </form>

      {loading ? (
        <div className="admin-stats-loading">Loading...</div>
      ) : (
        <>
          <h4 className="admin-stats-subtitle">All Stats</h4>
          <div className="admin-stats-table-wrapper">
          <table className="admin-stats-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Value</th>
                <th>Logo</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((stat) => (
                <tr key={stat.id}>
                  <td>{stat.id}</td>
                  <td>{stat.value}</td>
                  <td>
                    {stat.logo && (
                      <img
                        src={stat.logo}
                        alt={stat.id}
                        className="admin-stats-thumbnail"
                      />
                    )}
                  </td>
                  <td className="admin-stats-actions">
                    <button
                      className="admin-stats-edit"
                      onClick={() => handleEdit(stat)}
                    >
                      Edit
                    </button>
                    {stat.id !== 'Website viewers' && (
                      <button
                        className="admin-stats-delete"
                        onClick={() => handleDelete(stat.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Stats;
